// ===== rules/rules-generic.ts ======================================================
import { CoreModifier, AdjustedLine, ModifierContext } from '../modifier-models';
import { BaseRule } from '../rule-base';

// Add a constant number of units
export class AddFixedUnitsRule extends BaseRule {
    name: string;
    constructor(
        public readonly code: CoreModifier,
        private readonly addUnits: number
    ) {
        super();
        this.name = `${code} +${addUnits}u`;
    }

    run(line: AdjustedLine, ctx: ModifierContext, autoApply: boolean): void {
        if (line.code !== this.code) return;

        const totalPrice = ctx.totalPrice || 0;
        const totalUnits = ctx.totalUnits || 0;

        if (autoApply) {
            // Always use initialUnits as base to prevent compounding
            line.adjustedUnits = line.adjustedUnits ? this.addUnits : line.initialUnits ? +line.initialUnits + this.addUnits : this.addUnits;
            const pricePerUnit = totalPrice / totalUnits;
            line.adjustedRand = +(line.adjustedUnits * pricePerUnit).toFixed(2);
            line.trace.push(`${this.code} +${this.addUnits}u`);
        } else {
            line.suggestions = line.suggestions || [];
            line.suggestions.push({
                ruleCode: this.code,
                message: `Add ${this.addUnits} units`,
                kind: 'addition',
                unitsDelta: this.addUnits
            });
        }
    }

    apply(lines: AdjustedLine[], ctx: ModifierContext): AdjustedLine[] {


        const fullLineItems = ctx.lines?.filter(l => l.code === this.code);
        const inlineItems = ctx.lines?.filter(l => l.modifiers?.includes(this.code));

        if (fullLineItems?.length === 0 && inlineItems?.length === 0) {
            return lines;
        }

        if (fullLineItems && fullLineItems.length > 0) {
            const totalPrice = ctx.totalPrice || 0;
            const totalUnits = ctx.totalUnits || 0;
            const pricePerUnit = totalPrice / totalUnits;
            fullLineItems.forEach(l => {
                // check if the initial units and rand are the same as the total units and price
                // if they are, use the initial units and rand, otherwise use the total units and price to trigger 
                // running this rule
                let performedChanges = false;
                if (!l.initialUnits || !(l.initialUnits === totalUnits)) {
                    //run the rule
                    performedChanges = true;
                    l.adjustedUnits = this.addUnits;
                }

                if (!l.initialRand || !(l.initialRand === totalPrice)) {
                    //run the rule
                    performedChanges = true;
                    l.adjustedRand = +(l.adjustedUnits * pricePerUnit).toFixed(2);
                }

                if (performedChanges) {
                    l.trace.push(`${this.code} +${this.addUnits}u`);
                }
            });

        }

        if (inlineItems && inlineItems.length > 0) {

            inlineItems.forEach(l => {
                const totalPrice = l.initialRand || 0;
                const totalUnits = l.initialUnits || 0;
                const pricePerUnit = totalPrice / totalUnits;
                l.adjustedUnits = (l.initialUnits || 0) + this.addUnits;
                l.adjustedRand = +(l.adjustedUnits * pricePerUnit).toFixed(2);
                l.trace.push(`${this.code} +${this.addUnits}u`);
            });
        }


        return [...lines, ...(fullLineItems || []), ...(inlineItems || [])];
    }
}

/**
 * Reduce the TARGET procedure line to a fraction of its tariff.
 *
 * ⚠ `pct` is what remains, not what is taken off: 6302 (non-radiologist) is 0.6 because
 * the scan is billed at 60 % of the radiology tariff.
 *
 * ⚠ THE REDUCTION LANDS ON THE PROCEDURE, NEVER ON THE MODIFIER'S OWN LINE. A reduce
 * modifier carries 0 units of its own (see the procedure codebook, ProcedureCodes), so the earlier
 * `l.code === this.code` filter scaled 0 × 0.6 and left the scan at 100 % — every reduce
 * modifier was silently inert in both shapes. This mirrors AddPercentageRule, which had
 * both shapes right all along; the two directions must stay symmetrical.
 */
export class PercentReduceRule extends BaseRule {
    override name: string;
    constructor(
        public readonly code: CoreModifier,
        private readonly pct: number // e.g. 0.6 → charge 60 %
    ) {
        super();
        this.name = `${code} ×${pct}`;
    }

    run(line: AdjustedLine, ctx: ModifierContext, autoApply: boolean): void {
        // Modifier attached in-line to the procedure it reduces.
        if (!line.isModifier && !line.isConsult && line.modifiers?.includes(this.code)) {
            if (autoApply) {
                line.adjustedUnits *= this.pct;
                line.trace.push(`${this.code} ×${this.pct}`);
            } else {
                line.suggestions = line.suggestions || [];
                line.suggestions.push({
                    ruleCode: this.code,
                    message: `Reduce to ${(this.pct * 100).toFixed(0)}% of tariff`,
                    kind: 'overwrite',
                    unitsDelta: line.adjustedUnits * this.pct
                });
            }
            return;
        }

        // Modifier supplied as its own line — it never bills on its own.
        if (line.isModifier && line.code === this.code) {
            if (autoApply) {
                line.adjustedUnits = 0;
                line.trace.push('absorbed');
            } else {
                line.suggestions = line.suggestions || [];
                line.suggestions.push({
                    ruleCode: this.code,
                    message: 'Modifier will be absorbed into target line',
                    kind: 'overwrite',
                    unitsDelta: 0
                });
            }
        }
    }

    apply(lines: AdjustedLine[], ctx: ModifierContext): AdjustedLine[] {
        // ① modifier attached in-line
        lines
            .filter(l => !l.isModifier && !l.isConsult && l.modifiers?.includes(this.code))
            .forEach(l => {
                l.adjustedUnits *= this.pct;
                l.trace.push(`${this.code} ×${this.pct}`);
            });

        // ② modifier supplied as its own line (isModifier)
        lines
            .filter(l => l.isModifier && l.code === this.code)
            .forEach(mod => {
                const idx = lines.indexOf(mod);
                const target = lines.slice(0, idx).reverse().find(x => !x.isModifier);
                if (target) {
                    target.adjustedUnits *= this.pct;
                    target.trace.push(`${this.code} ×${this.pct}`);
                    mod.adjustedUnits = 0;             // never bills alone
                    mod.trace.push('absorbed');
                }
            });

        return lines;
    }
}

// Informational modifier – optionally warn if missing
export class InfoValidatorRule extends BaseRule {
    override name: string;
    constructor(
        public readonly code: CoreModifier,
        private readonly mustExist = false
    ) {
        super();
        this.name = `${code} info`;
    }

    run(line: AdjustedLine, ctx: ModifierContext, autoApply: boolean): void {
        // Since we need to check if the modifier exists in any line,
        // we'll need to rely on the apply method for this rule
        if (autoApply) {
            line.trace.push(`${this.code}: Requires full context for validation`);
        } else {
            line.suggestions = line.suggestions || [];
            line.suggestions.push({
                ruleCode: this.code,
                message: 'Modifier validation requires full context',
                kind: 'overwrite'
            });
        }
    }

    apply(lines: AdjustedLine[], ctx: ModifierContext, warn: string[]): AdjustedLine[] {
        if (this.mustExist && !lines.some(l => l.modifiers?.includes(this.code))) {
            warn.push(`${this.code} expected but not found.`);
        }
        return lines;
    }
}
